"use client"
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { getAuthHeader } from "../utils/auth";
import { useBranding } from "../hooks/useBranding";
import { isEnglishOperatorMode } from "../utils/operatorMode";

// --- SVG Icons ---
const SaveIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>;
const BuildingIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 22h16"/><path d="M2 11h20"/><path d="M17 3H7a2 2 0 0 0-2 2v14h14V5a2 2 0 0 0-2-2z"/><path d="M12 2v20"/></svg>;
const BanIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line></svg>;
const ClockIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>;
const DeleteIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>;
const EditIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"></path><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path></svg>;
const DownloadIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>;
const PlusCircleIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="16"></line><line x1="8" y1="12" x2="16" y2="12"></line></svg>;
const GlobeIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="2" y1="12" x2="22" y2="12"></line><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path></svg>;

const API_URL = window.location.hostname === 'localhost' 
  ? (process.env.REACT_APP_API_URL || "http://localhost:3173") 
  : window.location.origin;

function CompanyProfileForm({ profile, onSave, onCancel, clientId, users, isEnglishBeta = false }) {
    const [form, setForm] = useState(profile);
    const copy = isEnglishBeta ? {
        templateInfo: "Profile information",
        templateName: "Profile name",
        staff: "Operator",
        select: "Select",
        companyInfo: "Company information",
        company: "Company name",
        department: "Department",
        position: "Title",
        employeeCount: "Employee count",
        homepage: "Website",
        contactInfo: "Sender information for Japanese outreach copy",
        lastName: "Last name",
        firstName: "First name",
        lastNameKana: "Last name kana",
        firstNameKana: "First name kana",
        email: "Email",
        addressInfo: "Contact and address",
        tel1: "Phone 1",
        tel2: "Phone 2",
        tel3: "Phone 3",
        postalCode: "Postal code",
        pref: "Prefecture",
        address1: "City / street address",
        address2: "Building",
        cancel: "Cancel",
        save: "Save profile"
    } : {
        templateInfo: "テンプレート情報",
        templateName: "テンプレート名",
        staff: "担当スタッフ",
        select: "選択してください",
        companyInfo: "会社情報",
        company: "会社名",
        department: "部署名",
        position: "役職",
        employeeCount: "従業員数",
        homepage: "ホームページ",
        contactInfo: "担当者情報（差し込み用）",
        lastName: "姓",
        firstName: "名",
        lastNameKana: "セイ",
        firstNameKana: "メイ",
        email: "メールアドレス",
        emailNote: "フォーム送信後にサンクスメールや返信が大量に届く可能性があります。通常業務用ではなく、受信用の専用アドレスを推奨します（フリーアドレスは非推奨）。",
        addressInfo: "連絡先・住所",
        tel1: "電話番号1",
        tel2: "電話番号2",
        tel3: "電話番号3",
        postalCode: "郵便番号",
        pref: "都道府県",
        address1: "市区町村番地",
        address2: "建物名",
        cancel: "キャンセル",
        save: "保存"
    };

    useEffect(() => {
        setForm(profile);
    }, [profile]);

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave({ ...form, client_id: clientId });
    };

    return (
        <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-md space-y-8 mt-4">
            <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">{copy.templateInfo}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="text-sm font-medium text-gray-600 block mb-1">{copy.templateName} <span className="text-red-500">*</span></label>
                        <input type="text" name="profile_name" required value={form.profile_name || ''} onChange={handleChange} className="w-full p-2 border rounded-md"/>
                    </div>
                    <div>
                        <label className="text-sm font-medium text-gray-600 block mb-1">{copy.staff} <span className="text-red-500">*</span></label>
                        <select name="staff_email" required value={form.staff_email || ''} onChange={handleChange} className="w-full p-2 border rounded-md bg-white">
                            <option value="">{copy.select}</option>
                            {users.map(u => (<option key={u.id} value={u.email}>{u.name}</option>))}
                        </select>
                    </div>
                </div>
            </div>
            <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">{copy.companyInfo}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div><label className="text-sm font-medium text-gray-600 block mb-1">{copy.company}</label><input type="text" name="company" value={form.company || ''} onChange={handleChange} className="w-full p-2 border rounded-md"/></div>
                    <div><label className="text-sm font-medium text-gray-600 block mb-1">{copy.department}</label><input type="text" name="department" value={form.department || ''} onChange={handleChange} className="w-full p-2 border rounded-md"/></div>
                    <div><label className="text-sm font-medium text-gray-600 block mb-1">{copy.position}</label><input type="text" name="position" value={form.position || ''} onChange={handleChange} className="w-full p-2 border rounded-md"/></div>
                    <div><label className="text-sm font-medium text-gray-600 block mb-1">{copy.employeeCount}</label><input type="text" name="employee_count" value={form.employee_count || ''} onChange={handleChange} className="w-full p-2 border rounded-md"/></div>
                    <div className="md:col-span-2"><label className="text-sm font-medium text-gray-600 block mb-1">{copy.homepage}</label><input type="text" name="homepage" value={form.homepage || ''} onChange={handleChange} className="w-full p-2 border rounded-md"/></div>
                </div>
            </div>
            <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">{copy.contactInfo}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div><label className="text-sm font-medium text-gray-600 block mb-1">{copy.lastName}</label><input type="text" name="last_name" value={form.last_name || ''} onChange={handleChange} className="w-full p-2 border rounded-md"/></div>
                    <div><label className="text-sm font-medium text-gray-600 block mb-1">{copy.firstName}</label><input type="text" name="first_name" value={form.first_name || ''} onChange={handleChange} className="w-full p-2 border rounded-md"/></div>
                    <div><label className="text-sm font-medium text-gray-600 block mb-1">{copy.lastNameKana}</label><input type="text" name="last_name_kana" value={form.last_name_kana || ''} onChange={handleChange} className="w-full p-2 border rounded-md"/></div>
                    <div><label className="text-sm font-medium text-gray-600 block mb-1">{copy.firstNameKana}</label><input type="text" name="first_name_kana" value={form.first_name_kana || ''} onChange={handleChange} className="w-full p-2 border rounded-md"/></div>
                    <div className="md:col-span-2">
                        <label className="text-sm font-medium text-gray-600 block mb-1">{copy.email}</label>
                        <input type="email" name="email" value={form.email || ''} onChange={handleChange} className="w-full p-2 border rounded-md"/>
                        {!isEnglishBeta && <p className="mt-1 text-xs leading-5 text-amber-700">{copy.emailNote}</p>}
                    </div>
                </div>
            </div>
            <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">{copy.addressInfo}</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div><label className="text-sm font-medium text-gray-600 block mb-1">{copy.tel1}</label><input type="text" name="tel1" value={form.tel1 || ''} onChange={handleChange} className="w-full p-2 border rounded-md" placeholder="03" maxLength="4" pattern="\d*"/></div>
                    <div><label className="text-sm font-medium text-gray-600 block mb-1">{copy.tel2}</label><input type="text" name="tel2" value={form.tel2 || ''} onChange={handleChange} className="w-full p-2 border rounded-md" placeholder="1234" maxLength="4" pattern="\d*"/></div>
                    <div><label className="text-sm font-medium text-gray-600 block mb-1">{copy.tel3}</label><input type="text" name="tel3" value={form.tel3 || ''} onChange={handleChange} className="w-full p-2 border rounded-md" placeholder="5678" maxLength="4" pattern="\d*"/></div>
                    <div className="md:col-span-2"><label className="text-sm font-medium text-gray-600 block mb-1">{copy.postalCode}</label><div className="flex items-center gap-2"><input type="text" name="zip1" value={form.zip1 || ''} onChange={handleChange} className="w-1/3 p-2 border rounded-md" placeholder="100" maxLength="3" pattern="\d*"/> - <input type="text" name="zip2" value={form.zip2 || ''} onChange={handleChange} className="w-2/3 p-2 border rounded-md" placeholder="0001" maxLength="4" pattern="\d*"/></div></div>
                    <div><label className="text-sm font-medium text-gray-600 block mb-1">{copy.pref}</label><input type="text" name="pref" value={form.pref || ''} onChange={handleChange} className="w-full p-2 border rounded-md"/></div>
                    <div className="md:col-span-3"><label className="text-sm font-medium text-gray-600 block mb-1">{copy.address1}</label><input type="text" name="address1" value={form.address1 || ''} onChange={handleChange} className="w-full p-2 border rounded-md"/></div>
                    <div className="md:col-span-3"><label className="text-sm font-medium text-gray-600 block mb-1">{copy.address2}</label><input type="text" name="address2" value={form.address2 || ''} onChange={handleChange} className="w-full p-2 border rounded-md"/></div>
                </div>
            </div>
            <div className="pt-4 border-t mt-4 flex justify-end gap-2">
                <button type="button" onClick={onCancel} className="bg-gray-200 text-gray-700 font-semibold py-2 px-4 rounded-md hover:bg-gray-300">{copy.cancel}</button>
                <button type="submit" className="flex items-center gap-2 bg-blue-600 text-white font-semibold py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"><SaveIcon/> {copy.save}</button>
            </div>
        </form>
    );
}

// --- 日程調整設定コンポーネント ---
function SchedulingSettings({ clientId, currentUrl, onSave }) {
    const { appName } = useBranding();
    const [timerexUrl, setTimerexUrl] = useState(currentUrl || "");
    const [isCopied, setIsCopied] = useState(false);

    const webhookUrl = `${API_URL}/api/webhooks/timerex/${clientId}`;

    useEffect(() => {
        if (currentUrl) {
            setTimerexUrl(currentUrl);
        }
    }, [currentUrl]);

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(timerexUrl);
    };

    const handleCopyWebhookUrl = () => {
        navigator.clipboard.writeText(webhookUrl);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
    };

    return (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-md space-y-4">
                <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">
                    日程調整ツールの設定
                </h3>
                <p className="text-sm text-gray-600">
                    TimeRexなどで発行した「あなたの予約ページURL」を登録してください。<br />
                    AIが作成する営業メールや、拡張機能からの送信時に、このURLが自動的に参照されます。
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="text-sm font-medium text-gray-600 block mb-1">
                            日程調整URL (TimeRexなど)
                        </label>
                        <div className="flex gap-2">
                            <div className="relative flex-grow">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                                </div>
                                <input
                                    type="url"
                                    placeholder="https://timerex.net/s/your-id/..."
                                    value={timerexUrl}
                                    onChange={(e) => setTimerexUrl(e.target.value)}
                                    className="w-full pl-10 p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                        </div>
                    </div>
                    <div className="flex justify-end">
                        <button type="submit" className="flex items-center gap-2 bg-blue-600 text-white font-semibold py-2 px-4 rounded-md hover:bg-blue-700 transition-colors">
                            <SaveIcon /> 保存する
                        </button>
                    </div>
                </form>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md space-y-6">
                <div className="border-b pb-2">
                    <h3 className="text-lg font-semibold text-gray-700">
                        成果通知（Webhook）の連携設定
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">
                        ※この設定を行うことで、予約が入った際に{appName}上のステータスが自動で「アポイント取得」に更新されます。
                    </p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                    <h4 className="text-sm font-bold text-blue-800 mb-2">あなた専用のWebhook URL</h4>
                    <div className="flex gap-2">
                        <code className="flex-grow bg-white p-3 rounded border border-blue-200 text-sm text-gray-600 font-mono break-all">
                            {webhookUrl}
                        </code>
                        <button
                            onClick={handleCopyWebhookUrl}
                            className="flex-shrink-0 bg-blue-600 text-white text-sm font-bold px-4 rounded hover:bg-blue-700 transition-colors"
                        >
                            {isCopied ? "コピー完了！" : "コピー"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Pagination({ currentPage, totalPages, onPageChange }) {
    if (totalPages <= 1) return null;
    return (
        <div className="flex flex-wrap items-center justify-center gap-2">
            <button onClick={() => onPageChange(1)} disabled={currentPage === 1} className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">最初</button>
            <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">前へ</button>
            <span className="text-sm text-gray-700">{currentPage} / {totalPages}</span>
            <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">次へ</button>
            <button onClick={() => onPageChange(totalPages)} disabled={currentPage === totalPages} className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">最後</button>
        </div>
    );
}

function GlobalNgListManagement() {
    const [globalNgList, setGlobalNgList] = useState([]);
    const [newGlobalNg, setNewGlobalNg] = useState({ domain: "", company_name: "", reason: "" });
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [pagination, setPagination] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState(true);
    const { user } = useOutletContext(); 


    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            setCurrentPage(1); 
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    const loadGlobalNgList = useCallback((page = 1, term = "") => {
        setIsLoading(true);
        const params = new URLSearchParams({ page, limit: 50, searchTerm: term });
        fetch(`${API_URL}/api/admin/global-ng-list?${params.toString()}`, { headers: getAuthHeader() })
            .then(res => res.json())
            .then(data => {
                if (data.success) {
                    setGlobalNgList(data.data || []);
                    setPagination(data.pagination || null);
                }
            })
            .finally(() => setIsLoading(false));
    }, []);

    useEffect(() => {
        loadGlobalNgList(currentPage, debouncedSearchTerm);
    }, [currentPage, debouncedSearchTerm, loadGlobalNgList]);

    const handlePageChange = (newPage) => {
        if (newPage > 0 && pagination && newPage <= pagination.totalPages) {
            setCurrentPage(newPage);
        }
    };

    const handleSearch = () => {
        setCurrentPage(1);
        loadGlobalNgList(1, searchTerm);
    };
    
    const handleAddGlobalNg = async (e) => {
        e.preventDefault();
        if (!newGlobalNg.domain) {
            alert("ドメインは必須です。");
            return;
        }
        try {
            const payload = {
                ...newGlobalNg,
                added_by: user.email 
            };
            const res = await fetch(`${API_URL}/api/admin/global-ng-list`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...getAuthHeader() },
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error);
            setNewGlobalNg({ domain: "", company_name: "", reason: "" });
            loadGlobalNgList(currentPage, debouncedSearchTerm);
        } catch (err) {
            alert(`追加エラー: ${err.message}`);
        }
    };

    const handleDeleteGlobalNg = async (id) => {
        if (!window.confirm("このグローバルNG項目を削除しますか？\nこのドメインは全てのクライアントの送信対象から除外されます。")) return;
        try {
            const res = await fetch(`${API_URL}/api/admin/global-ng-list/${id}`, {
                method: "DELETE",
                headers: getAuthHeader()
            });
            if (!res.ok) throw new Error("削除に失敗しました。");
            loadGlobalNgList(currentPage, debouncedSearchTerm);
        } catch (err) {
            alert(`削除エラー: ${err.message}`);
        }
    };

    return (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">グローバルNGリストに手動追加</h3>
                <p className="text-sm text-gray-600 mb-4">ここに登録されたドメインは、全てのクライアントの送信対象から恒久的に除外されます。ドメインは `example.com` の形式で入力してください。</p>
                <form onSubmit={handleAddGlobalNg} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
                    <input type="text" placeholder="ドメイン (必須)" required value={newGlobalNg.domain} onChange={(e) => setNewGlobalNg({...newGlobalNg, domain: e.target.value})} className="w-full p-2 border rounded-md"/>
                    <input type="text" placeholder="会社名 (任意)" value={newGlobalNg.company_name} onChange={(e) => setNewGlobalNg({...newGlobalNg, company_name: e.target.value})} className="w-full p-2 border rounded-md"/>
                    <input type="text" placeholder="理由 (任意)" value={newGlobalNg.reason} onChange={(e) => setNewGlobalNg({...newGlobalNg, reason: e.target.value})} className="w-full p-2 border rounded-md col-span-1 md:col-span-2 lg:col-span-2"/>
                    <button type="submit" className="bg-blue-600 text-white font-semibold py-2 px-4 rounded-md hover:bg-blue-700 transition-colors">追加</button>
                </form>
            </div>
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="p-4 border-b">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                        <input
                            type="text"
                            placeholder="ドメイン、会社名、理由などで検索..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                            className="w-full p-2 border rounded-md"
                        />
                        <button onClick={handleSearch} className="bg-blue-600 text-white font-semibold py-2 px-4 rounded-md hover:bg-blue-700 whitespace-nowrap">検索</button>
                    </div>
                </div>
                <div className="overflow-auto" style={{ maxHeight: '60vh' }}>
                    <div className="divide-y divide-gray-100 md:hidden">
                        {isLoading ? (
                            <div className="p-8 text-center text-sm text-gray-500">読み込み中...</div>
                        ) : globalNgList.length > 0 ? (
                            globalNgList.map(item => (
                                <div key={`${item.id}-mobile`} className="p-4">
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <p className="font-mono font-semibold text-gray-900 break-all">{item.domain}</p>
                                            <p className="mt-1 text-xs text-gray-500 break-words">{item.company_name || '会社名なし'}</p>
                                        </div>
                                        <button onClick={() => handleDeleteGlobalNg(item.id)} className="flex-shrink-0 rounded-full bg-red-50 p-2 text-red-500 hover:bg-red-100">
                                            <DeleteIcon/>
                                        </button>
                                    </div>
                                    <div className="mt-3 space-y-1 text-xs text-gray-500">
                                        <p className="break-words">理由: {item.reason || 'ー'}</p>
                                        <p className="break-all">登録者: {item.added_by || 'ー'}</p>
                                        <p>登録日: {new Date(item.created_at).toLocaleDateString('ja-JP')}</p>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="p-8 text-center text-sm text-gray-500">該当するデータはありません。</div>
                        )}
                    </div>
                    <table className="hidden w-full text-sm text-left text-gray-600 md:table">
                        <thead className="bg-gray-100 text-xs text-gray-700 uppercase sticky top-0">
                            <tr>
                                <th className="py-3 px-4">ドメイン</th>
                                <th className="py-3 px-4">会社名</th>
                                <th className="py-3 px-4">理由</th>
                                <th className="py-3 px-4">登録者</th>
                                <th className="py-3 px-4">登録日</th>
                                <th className="py-3 px-4 w-20">操作</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {isLoading ? (
                                <tr><td colSpan="6" className="text-center p-8">読み込み中...</td></tr>
                            ) : globalNgList.length > 0 ? (
                                globalNgList.map(item => (
                                    <tr key={item.id} className="hover:bg-gray-50">
                                        <td className="py-3 px-4 font-mono">{item.domain}</td>
                                        <td className="py-3 px-4 font-medium">{item.company_name || 'ー'}</td>
                                        <td className="py-3 px-4">{item.reason || 'ー'}</td>
                                        <td className="py-3 px-4">{item.added_by || 'ー'}</td>
                                        <td className="py-3 px-4">{new Date(item.created_at).toLocaleDateString('ja-JP')}</td>
                                        <td className="py-3 px-4">
                                            <button onClick={() => handleDeleteGlobalNg(item.id)} className="p-2 text-red-500 hover:bg-red-100 rounded-full">
                                                <DeleteIcon/>
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan="6" className="text-center py-8 text-gray-500">該当するデータはありません。</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
                {pagination && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={pagination.totalPages}
                        onPageChange={handlePageChange}
                    />
                )}
            </div>
        </div>
    );
}

function SettingsPage() {
    const { user } = useOutletContext();
    const isEnglishBeta = isEnglishOperatorMode();
    const [activeTab, setActiveTab] = useState("company");
    const [companyProfiles, setCompanyProfiles] = useState([]);
    const [editingProfile, setEditingProfile] = useState(null);
    const [users, setUsers] = useState([]);
    const [ngList, setNgList] = useState([]);
    const [newNg, setNewNg] = useState({ company: "", url_pattern: "", domain: "", reason: "" }); 
    const [csvFile, setCsvFile] = useState(null);
    const [ngSearchTerm, setNgSearchTerm] = useState("");
    const [ngPagination, setNgPagination] = useState(null);
    const [ngCurrentPage, setNgCurrentPage] = useState(1);
    const [excludedDays, setExcludedDays] = useState([]);
    const [excludedHours, setExcludedHours] = useState({ from: "00:00", to: "00:00" });
    const [editingNgId, setEditingNgId] = useState(null);

    const [selectedNgIds, setSelectedNgIds] = useState([]);
    const [isDeletingNg, setIsDeletingNg] = useState(false);
    
    // 日程調整URL用のState
    const [schedulingUrl, setSchedulingUrl] = useState("");

    const selectedClientId = useMemo(() => {
        // 本家管理者、またはOEM管理者の場合（oem_idを持っている）
        // localStorage に選択中のクライアントIDがあればそれを優先する
        if (user?.role === "admin" || user?.role === "admin_staff" || user?.oem_id) {
            return localStorage.getItem("selectedClientId") || user?.client_id;
        }
        return user?.client_id;
    }, [user]);
    const fetchCompanyProfiles = useCallback(() => {
        if (!selectedClientId) return;
        fetch(`${API_URL}/api/company-profiles?client_id=${selectedClientId}`, { headers: getAuthHeader() })
            .then(res => res.json())
            .then(data => {
                if (data.success) setCompanyProfiles(data.profiles || []);
            });
    }, [selectedClientId]);

    const fetchUsers = useCallback(() => {
        if (!selectedClientId) return;
        fetch(`${API_URL}/api/users?client_id=${selectedClientId}`, { headers: getAuthHeader() }).then(res => res.json())
            .then(data => { if (data.success) setUsers(data.users || []); });
    }, [selectedClientId]);

    const loadNgList = useCallback((page = 1, term = "") => {
        if (!selectedClientId) return;
        const params = new URLSearchParams({ client_id: selectedClientId, page, limit: 50, searchTerm: term });
        fetch(`${API_URL}/api/ng-list?${params.toString()}`, { headers: getAuthHeader() })
            .then(res => res.json())
            .then(data => {
                if (data.success) {
                    setNgList(data.data || []);
                    setNgPagination(data.pagination || null);
                }
            });
    }, [selectedClientId]);

    const fetchExclusionSettings = useCallback(() => {
        if (!selectedClientId) return;
        fetch(`${API_URL}/api/excluded-settings?client_id=${selectedClientId}`, { headers: getAuthHeader() })
            .then(res => res.json())
            .then(data => {
                if (data.success && data.settings) {
                    setExcludedDays(data.settings.excluded_days?.split(',').filter(Boolean) || []);
                    setExcludedHours({ from: data.settings.excluded_hours_from || "00:00", to: data.settings.excluded_hours_to || "00:00" });
                }
            });
    }, [selectedClientId]);

    const fetchClientInfo = useCallback(() => {
        if (!selectedClientId) return;
        fetch(`${API_URL}/api/client-info?client_id=${selectedClientId}`, { headers: getAuthHeader() })
            .then(res => res.json())
            .then(data => {
                if (data.success && data.info) {
                    setSchedulingUrl(data.info.scheduling_url || "");
                }
            })
            .catch(err => console.error("クライアント情報の取得に失敗:", err));
    }, [selectedClientId]);

    useEffect(() => {
        if (selectedClientId) {
            fetchCompanyProfiles();
            fetchUsers();
            loadNgList(ngCurrentPage, ngSearchTerm);
            fetchExclusionSettings();
            fetchClientInfo();
        }
    }, [selectedClientId, ngCurrentPage, ngSearchTerm, fetchCompanyProfiles, fetchUsers, loadNgList, fetchExclusionSettings, fetchClientInfo]);

    const handleSaveProfile = async (profileData) => {
        const url = profileData.id ? `${API_URL}/api/company-profiles/${profileData.id}` : `${API_URL}/api/company-profiles`;
        const method = profileData.id ? 'PUT' : 'POST';
        try {
            const res = await fetch(url, {
                method,
                headers: { "Content-Type": "application/json", ...getAuthHeader() },
                body: JSON.stringify(profileData)
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || (isEnglishBeta ? 'Save failed.' : '保存に失敗しました'));
            alert(isEnglishBeta ? "Company profile saved." : "会社概要テンプレートを保存しました！");
            setEditingProfile(null);
            fetchCompanyProfiles();
        } catch (err) {
            alert(err.message);
        }
    };

    const handleDeleteProfile = async (profileId) => {
        if (!window.confirm(isEnglishBeta ? "Delete this company profile? Profiles used by templates cannot be deleted." : "この会社概要テンプレートを削除しますか？\n（このテンプレートを使用している文面テンプレートがあると削除できません）")) return;
        try {
            const res = await fetch(`${API_URL}/api/company-profiles/${profileId}`, { method: 'DELETE', headers: getAuthHeader() });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || (isEnglishBeta ? 'Delete failed.' : '削除に失敗しました'));
            alert(isEnglishBeta ? "Company profile deleted." : data.message);
            fetchCompanyProfiles();
        } catch (err) {
            alert(err.message);
        }
    };

    const handleSaveExcludedSettings = async (e) => { e.preventDefault(); try { const res = await fetch(`${API_URL}/api/excluded-settings`, { method: "POST", headers: { "Content-Type": "application/json", ...getAuthHeader() }, body: JSON.stringify({ client_id: selectedClientId, excluded_days: excludedDays.join(','), excluded_hours_from: excludedHours.from, excluded_hours_to: excludedHours.to }) }); if (!res.ok) throw new Error(isEnglishBeta ? 'Save failed.' : '保存に失敗しました'); alert(isEnglishBeta ? "Execution exclusion settings saved." : "除外設定を保存しました！"); } catch { alert(isEnglishBeta ? "Failed to save execution exclusion settings." : "除外設定の保存に失敗しました。"); } };
    
    const handleAddNg = () => { if (!newNg.company && !newNg.url_pattern && !newNg.domain) return alert(isEnglishBeta ? "Enter a company, URL pattern, or domain." : "会社名、URLパターン、ドメインのいずれかを入力してください"); fetch(`${API_URL}/api/ng-list`, { method: "POST", headers: { "Content-Type": "application/json", ...getAuthHeader() }, body: JSON.stringify({ ...newNg, client_id: selectedClientId }) }).then(res => res.json()).then(data => { if(data.success) { setNewNg({ company: "", url_pattern: "", domain: "", reason: "" }); loadNgList(ngCurrentPage, ngSearchTerm); } else { alert(isEnglishBeta ? "Failed to add suppression item." : "NGリストの追加に失敗しました。"); } }); };
    const handleUpdateNg = async () => { if (!editingNgId) return; if (!newNg.company && !newNg.url_pattern && !newNg.domain) return alert(isEnglishBeta ? "Enter a company, URL pattern, or domain." : "会社名、URLパターン、ドメインのいずれかを入力してください"); try { const res = await fetch(`${API_URL}/api/ng-list/${editingNgId}`, { method: 'PUT', headers: { "Content-Type": "application/json", ...getAuthHeader() }, body: JSON.stringify({ ...newNg, client_id: selectedClientId }) }); const data = await res.json(); if (!res.ok) throw new Error(data.error || (isEnglishBeta ? 'Update failed.' : '更新に失敗しました')); alert(isEnglishBeta ? "Suppression item updated." : "NGリスト項目を更新しました。"); setEditingNgId(null); setNewNg({ company: "", url_pattern: "", domain: "", reason: "" }); loadNgList(ngCurrentPage, ngSearchTerm); } catch (err) { alert(isEnglishBeta ? `Update error: ${err.message}` : `更新エラー: ${err.message}`); } };
    const handleNgSubmit = (e) => { e.preventDefault(); if (editingNgId) { handleUpdateNg(); } else { handleAddNg(); } };
    const handleEditNgClick = (item) => { setEditingNgId(item.id); setNewNg({ company: item.company || "", url_pattern: item.url_pattern || "", domain: item.domain || "", reason: item.reason || "" }); const formElement = document.getElementById("ng-list-form"); if (formElement) { formElement.scrollIntoView({ behavior: 'smooth', block: 'start' }); } };
    const handleCancelEditNg = () => { setEditingNgId(null); setNewNg({ company: "", url_pattern: "", domain: "", reason: "" }); };
    const handleDeleteNg = (id) => { if (!window.confirm(isEnglishBeta ? "Delete this suppression item?" : "このNGリスト項目を削除しますか？")) return; fetch(`${API_URL}/api/ng-list/${id}`, { method: "DELETE", headers: getAuthHeader() }).then(() => loadNgList(ngCurrentPage, ngSearchTerm)); };
    const handleBulkDeleteNg = async () => {
        if (selectedNgIds.length === 0) return;
        if (!window.confirm(isEnglishBeta ? `Delete ${selectedNgIds.length} selected suppression items? This cannot be undone.` : `選択した ${selectedNgIds.length} 件のNGリスト項目を本当に一括削除しますか？\n※この操作は取り消せません。`)) return;

        setIsDeletingNg(true);
        try {
            const res = await fetch(`${API_URL}/api/ng-list/bulk-delete`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...getAuthHeader(),
                },
                body: JSON.stringify({ client_id: selectedClientId, ids: selectedNgIds })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error);
            
            alert(data.message);
            setSelectedNgIds([]); // 選択をクリア
            loadNgList(ngCurrentPage, ngSearchTerm); // リストを再取得
        } catch (err) {
            alert(isEnglishBeta ? `Bulk delete error: ${err.message}` : `一括削除エラー: ${err.message}`);
        } finally {
            setIsDeletingNg(false);
        }
    };

    // ★追加: NGリストの全選択/解除
    const handleNgSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedNgIds(ngList.map(item => item.id));
        } else {
            setSelectedNgIds([]);
        }
    };

    // ★追加: NGリストの個別選択/解除
    const handleNgSelectOne = (e, id) => {
        if (e.target.checked) {
            setSelectedNgIds(prev => [...prev, id]);
        } else {
            setSelectedNgIds(prev => prev.filter(itemId => itemId !== id));
        }
    };

    // ★追加: ページ遷移・検索時に選択状態をリセット
    useEffect(() => {
        setSelectedNgIds([]);
    }, [ngList]);
    const handleBulkUpload = async () => { if (!csvFile) return alert(isEnglishBeta ? "Select a CSV file." : "ファイルを選択してください"); const formData = new FormData(); formData.append("file", csvFile); formData.append("client_id", selectedClientId); try { const res = await fetch(`${API_URL}/api/ng-list-bulk`, { method: "POST", headers: getAuthHeader(), body: formData }); const result = await res.json(); if (result.success) { alert(isEnglishBeta ? `Upload complete. ${result.count} items added.` : `アップロード完了！${result.count}件登録`); loadNgList(); } else { alert(isEnglishBeta ? `Registration failed: ${result.error}` : `登録に失敗しました: ${result.error}`); } } catch { alert(isEnglishBeta ? "Network error." : "通信エラーが発生しました"); } };
    const handleDownloadTemplate = () => { fetch(`${API_URL}/api/ng-list-template`, { headers: getAuthHeader() }).then(response => { if (!response.ok) throw new Error(isEnglishBeta ? "Download failed." : "ダウンロードに失敗しました。"); return response.blob(); }).then(blob => { const url = window.URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = "suppression_template.csv"; document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url); }).catch(err => { console.error("NG list template download failed:", err); alert(err.message); }); };
    const handleNgPageChange = (newPage) => { if (newPage > 0 && ngPagination && newPage <= ngPagination.totalPages) { setNgCurrentPage(newPage); } };
    const handleNgSearch = () => { setNgCurrentPage(1); loadNgList(1, ngSearchTerm); };
    const handleExcludedDayChange = (day) => { setExcludedDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]); };
    const handleExcludedHourChange = (e) => setExcludedHours({...excludedHours, [e.target.name]: e.target.value});

    const handleSaveSchedulingUrl = async (url) => {
        try {
            const res = await fetch(`${API_URL}/api/client-info`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...getAuthHeader() },
                body: JSON.stringify({
                    client_id: selectedClientId,
                    scheduling_url: url 
                })
            });
            
            if (!res.ok) throw new Error('保存に失敗しました');
            alert("日程調整URLを保存しました！");
            fetchClientInfo(); 
        } catch (err) {
            alert(`保存エラー: ${err.message}`);
        }
    };

    const TabButton = ({ id, label, icon }) => ( <button type="button" onClick={() => setActiveTab(id)} className={`flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-t-lg transition-colors ${activeTab === id ? 'bg-white text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:bg-gray-200'}`}> {icon} {label} </button> );

    return (
        <div className="p-3 sm:p-4 md:p-8 bg-gray-50 min-h-full">
            <div className="max-w-7xl mx-auto">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">{isEnglishBeta ? "Company Profile & Suppression" : "各種設定"}</h1>
                {isEnglishBeta && (
                    <div className="mb-6 space-y-3">
                        <p className="max-w-3xl text-sm text-gray-600">
                            Define company context for Japanese outreach copy and maintain suppression rules before execution.
                        </p>
                        <div className="max-w-4xl rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
                            <p className="font-bold">Dedicated inbox recommended</p>
                            <p className="mt-1">
                                For high-volume outreach, use a dedicated inbox separate from your primary business email.
                                Replies, thank-you messages, and contact form notifications from Japanese companies may arrive in volume.
                            </p>
                        </div>
                    </div>
                )}
	                <div className="border-b border-gray-200 overflow-x-auto">
	                    <nav className="flex -mb-px min-w-max">
                        <TabButton id="company" label={isEnglishBeta ? "Company Profile" : "会社概要テンプレート"} icon={<BuildingIcon />} />
                        {/* Product management tab is intentionally hidden from the current direct-SaaS Agent flow. */}
                        {/* <TabButton id="scheduling" label="日程調整・連携" icon={<CalendarIcon />} /> */}
                        <TabButton id="nglist" label={isEnglishBeta ? "Suppression" : "NGリスト管理"} icon={<BanIcon />} />
                        {user?.role === 'admin' && !user?.oem_id && !isEnglishBeta && (
                            <TabButton id="globalNgList" label="グローバルNGリスト" icon={<GlobeIcon />} />
                        )}
                        <TabButton id="exclusion" label={isEnglishBeta ? "Execution exclusion" : "送信除外設定"} icon={<ClockIcon />} />
                    </nav>
                </div>

                <div className="mt-6">
                    {activeTab === 'company' && (
                        <div className="space-y-6">
                            {editingProfile ? (
                                <CompanyProfileForm 
                                    profile={editingProfile}
                                    onSave={handleSaveProfile}
                                    onCancel={() => setEditingProfile(null)}
                                    clientId={selectedClientId}
                                    users={users}
                                    isEnglishBeta={isEnglishBeta}
                                />
                            ) : (
                                <>
                                    <div className="flex justify-end">
	                                        <button onClick={() => setEditingProfile({ profile_name: '', staff_email: user.email })} className="flex w-full items-center justify-center gap-2 bg-blue-600 text-white font-semibold py-2 px-4 rounded-md hover:bg-blue-700 sm:w-auto">
                                            <PlusCircleIcon /> {isEnglishBeta ? "Create profile" : "新規テンプレート作成"}
                                        </button>
                                    </div>
	                                    <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md space-y-4">
                                        <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">{isEnglishBeta ? "Saved company profiles" : "保存済みテンプレート一覧"}</h3>
                                        {companyProfiles.length > 0 ? (
                                            companyProfiles.map(profile => (
	                                                <div key={profile.id} className="flex flex-col justify-between gap-3 p-3 border rounded-md hover:bg-gray-50 sm:flex-row sm:items-center">
	                                                    <div className="min-w-0">
	                                                        <p className="font-semibold">{profile.profile_name}</p>
	                                                        <p className="text-sm text-gray-500 break-all">{profile.company} / {users.find(u => u.email === profile.staff_email)?.name || profile.staff_email}</p>
	                                                    </div>
	                                                    <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center">
	                                                        <button onClick={() => setEditingProfile(profile)} className="rounded-md bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100">{isEnglishBeta ? "Edit" : "編集"}</button>
	                                                        <button onClick={() => handleDeleteProfile(profile.id)} className="rounded-md bg-red-50 px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-100">{isEnglishBeta ? "Delete" : "削除"}</button>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-gray-500 text-center py-4">{isEnglishBeta ? "No saved company profiles yet." : "保存されているテンプレートはありません。"}</p>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* {activeTab === 'scheduling' && (
                            <SchedulingSettings 
                                clientId={selectedClientId}
                                currentUrl={schedulingUrl} 
                                onSave={handleSaveSchedulingUrl}
                            />
                        )}
                        */}

                    {activeTab === 'nglist' && (
                        <div className="space-y-6">
	                            <div id="ng-list-form" className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
                                 <h3 className="text-lg font-semibold text-gray-700 mb-4">{editingNgId ? (isEnglishBeta ? 'Edit suppression item' : 'NGリスト項目を編集') : (isEnglishBeta ? 'Add suppression item manually' : 'NGリストに手動追加')}</h3>
                                 <form onSubmit={handleNgSubmit} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
                                     <input type="text" name="company" placeholder={isEnglishBeta ? "Company" : "会社名"} value={newNg.company} onChange={(e) => setNewNg({...newNg, company: e.target.value})} className="w-full p-2 border rounded-md"/>
                                     <input type="text" name="url_pattern" placeholder={isEnglishBeta ? "URL pattern" : "URLパターン"} value={newNg.url_pattern} onChange={(e) => setNewNg({...newNg, url_pattern: e.target.value})} className="w-full p-2 border rounded-md"/>
                                     <input type="text" name="domain" placeholder={isEnglishBeta ? "Domain" : "ドメイン"} value={newNg.domain} onChange={(e) => setNewNg({...newNg, domain: e.target.value})} className="w-full p-2 border rounded-md"/>
                                     <input type="text" name="reason" placeholder={isEnglishBeta ? "Reason (optional)" : "理由 (任意)"} value={newNg.reason} onChange={(e) => setNewNg({...newNg, reason: e.target.value})} className="w-full p-2 border rounded-md"/> 
                                     
	                                     <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                                        {editingNgId && (
                                            <button type="button" onClick={handleCancelEditNg} className="bg-gray-200 text-gray-700 font-semibold py-2 px-4 rounded-md hover:bg-gray-300 w-full justify-center">
                                                {isEnglishBeta ? "Cancel" : "キャンセル"}
                                            </button>
                                        )}
                                        <button type="submit" className="flex items-center gap-2 bg-blue-600 text-white font-semibold py-2 px-4 rounded-md hover:bg-blue-700 transition-colors w-full flex-grow justify-center">
                                            {editingNgId ? <SaveIcon /> : <PlusCircleIcon />}
                                            {editingNgId ? (isEnglishBeta ? 'Update' : '更新') : (isEnglishBeta ? 'Add' : '追加')}
                                        </button>
                                    </div>
                                 </form>
                            </div>

	                            <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
                                <h3 className="text-lg font-semibold text-gray-700 mb-4">{isEnglishBeta ? "Bulk import suppression items by CSV" : "NGリストをCSVで一括登録"}</h3>
                                <p className="text-sm text-gray-600 mb-4">{isEnglishBeta ? "Download the CSV template, edit it, then upload it here." : "以下のボタンから雛形ファイルをダウンロードし、編集してからアップロードしてください。"}</p>
	                                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
	                                    <button type="button" onClick={handleDownloadTemplate} className="flex items-center justify-center gap-2 bg-green-600 text-white font-semibold py-2 px-4 rounded-md hover:bg-green-700 transition-colors">
	                                        <DownloadIcon /> {isEnglishBeta ? "Download template" : "雛形ダウンロード"}
	                                    </button>
	                                    <input type="file" accept=".csv" onChange={(e) => setCsvFile(e.target.files[0])} className="text-sm"/>
	                                    <button type="button" onClick={handleBulkUpload} className="bg-blue-600 text-white font-semibold py-2 px-4 rounded-md hover:bg-blue-700 transition-colors">{isEnglishBeta ? "Upload" : "アップロード"}</button>
	                                </div>
	                            </div>
	                            <div className="bg-white rounded-lg shadow-md overflow-hidden">
	                                <div className="p-4 border-b flex flex-col justify-between gap-3 sm:flex-row sm:items-center sm:gap-4">
	                                    <div className="flex flex-col gap-2 flex-grow md:w-1/3 sm:flex-row sm:items-center">
                                        <input
                                            type="text"
                                            placeholder={isEnglishBeta ? "Search by company, domain, or reason..." : "会社名、ドメイン、理由などで検索..."}
                                            value={ngSearchTerm}
                                            onChange={(e) => setNgSearchTerm(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && handleNgSearch()}
                                            className="w-full p-2 border rounded-md"
                                        />
                                        <button onClick={handleNgSearch} className="bg-blue-600 text-white font-semibold py-2 px-4 rounded-md hover:bg-blue-700 whitespace-nowrap">{isEnglishBeta ? "Search" : "検索"}</button>
                                    </div>
                                    
                                    {/* ★追加: 一括削除ボタン */}
                                    {selectedNgIds.length > 0 && (
                                        <button 
                                            onClick={handleBulkDeleteNg} 
                                            disabled={isDeletingNg}
	                                            className="flex items-center justify-center gap-2 bg-red-100 text-red-600 font-bold py-2 px-4 rounded-md hover:bg-red-200 border border-red-200 transition-colors whitespace-nowrap"
                                        >
                                            <DeleteIcon />
                                            {isDeletingNg ? (isEnglishBeta ? "Deleting..." : "削除中...") : (isEnglishBeta ? `Delete ${selectedNgIds.length}` : `${selectedNgIds.length}件を一括削除`)}
                                        </button>
                                    )}
                                </div>
                                <div className="overflow-auto" style={{ maxHeight: '60vh' }}>
                                        <div className="divide-y divide-gray-100 md:hidden">
                                            {ngList.length > 0 ? (
                                                ngList.map(item => (
                                                    <div key={`${item.id}-mobile`} className={`p-4 ${selectedNgIds.includes(item.id) ? 'bg-blue-50/60' : ''}`}>
                                                        <div className="flex items-start gap-3">
                                                            <input type="checkbox" className="mt-1 h-5 w-5 flex-shrink-0 cursor-pointer" checked={selectedNgIds.includes(item.id)} onChange={(e) => handleNgSelectOne(e, item.id)} />
                                                            <div className="min-w-0 flex-1">
                                                                <p className="font-semibold text-gray-900 break-words">{item.company || '-'}</p>
                                                                <p className="mt-1 font-mono text-xs text-gray-500 break-all">{item.domain || '-'}</p>
                                                            </div>
                                                            <div className="flex flex-shrink-0 gap-1">
                                                                <button onClick={() => handleEditNgClick(item)} className="rounded-full bg-blue-50 p-2 text-blue-600 hover:bg-blue-100" title={isEnglishBeta ? "Edit" : "編集"}><EditIcon/></button>
                                                                <button onClick={() => handleDeleteNg(item.id)} className="rounded-full bg-red-50 p-2 text-red-500 hover:bg-red-100" title={isEnglishBeta ? "Delete" : "削除"}><DeleteIcon/></button>
                                                            </div>
                                                        </div>
                                                        <div className="mt-3 space-y-1 text-xs text-gray-500">
                                                            <p className="break-all">URL: {item.url_pattern || '-'}</p>
                                                            <p className="break-words">理由: {item.reason || '-'}</p>
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="p-8 text-center text-sm text-gray-500">{isEnglishBeta ? "No matching suppression items." : "該当するデータはありません。"}</div>
                                            )}
                                        </div>
	                                    <table className="hidden w-full text-sm text-left text-gray-600 md:table">
                                        <thead className="bg-gray-100 text-xs text-gray-700 uppercase sticky top-0 z-10 shadow-sm">
                                            <tr>
                                                {/* ★追加: ヘッダーの全選択チェックボックス */}
                                                <th className="py-3 px-4 text-center w-10">
                                                    <input 
                                                        type="checkbox" 
                                                        className="w-4 h-4 cursor-pointer"
                                                        onChange={handleNgSelectAll} 
                                                        checked={ngList.length > 0 && selectedNgIds.length === ngList.length} 
                                                    />
                                                </th>
                                                <th className="py-3 px-4">{isEnglishBeta ? "Company" : "会社名"}</th>
                                                <th className="py-3 px-4">{isEnglishBeta ? "URL pattern" : "URLパターン"}</th>
                                                <th className="py-3 px-4">{isEnglishBeta ? "Domain" : "ドメイン"}</th>
                                                <th className="py-3 px-4">{isEnglishBeta ? "Reason" : "理由"}</th>
                                                <th className="py-3 px-4 w-28 text-center">{isEnglishBeta ? "Actions" : "操作"}</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y">
                                            {ngList.length > 0 ? (
                                                ngList.map(item => (
                                                    <tr key={item.id} className={`hover:bg-gray-50 ${selectedNgIds.includes(item.id) ? 'bg-blue-50/50' : ''}`}>
                                                        {/* ★追加: 各行のチェックボックス */}
                                                        <td className="py-3 px-4 text-center">
                                                            <input 
                                                                type="checkbox" 
                                                                className="w-4 h-4 cursor-pointer"
                                                                checked={selectedNgIds.includes(item.id)} 
                                                                onChange={(e) => handleNgSelectOne(e, item.id)} 
                                                            />
                                                        </td>
                                                        <td className="py-3 px-4 font-medium">{item.company || '-'}</td>
                                                        <td className="py-3 px-4">{item.url_pattern || '-'}</td>
                                                        <td className="py-3 px-4">{item.domain || '-'}</td>
                                                        <td className="py-3 px-4">{item.reason || '-'}</td>
                                                        <td className="py-3 px-4">
                                                            <div className="flex justify-center items-center gap-1">
                                                                <button onClick={() => handleEditNgClick(item)} className="p-2 text-blue-600 hover:bg-blue-100 rounded-full" title={isEnglishBeta ? "Edit" : "編集"}>
                                                                    <EditIcon/>
                                                                </button>
                                                                <button onClick={() => handleDeleteNg(item.id)} className="p-2 text-red-500 hover:bg-red-100 rounded-full" title={isEnglishBeta ? "Delete" : "削除"}>
                                                                    <DeleteIcon/>
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    {/* セル数を6に変更 */}
                                                    <td colSpan="6" className="text-center py-8 text-gray-500">{isEnglishBeta ? "No matching suppression items." : "該当するデータはありません。"}</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
	                                <div className="p-4 border-t flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                    <span className="text-sm text-gray-600">
                                        {ngPagination ? (isEnglishBeta ? `${ngPagination.totalItems.toLocaleString()} items` : `${ngPagination.totalItems.toLocaleString()} 件中`) : ''}
                                    </span>
                                    <Pagination
                                        currentPage={ngCurrentPage}
                                        totalPages={ngPagination ? ngPagination.totalPages : 0}
                                        onPageChange={handleNgPageChange}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    {activeTab === 'globalNgList' && user?.role === 'admin' && !user?.oem_id && (
                        <GlobalNgListManagement />
                    )}
                    {activeTab === 'exclusion' && (
                         <div className="bg-white p-6 rounded-lg shadow-md">
                            <form onSubmit={handleSaveExcludedSettings}>
                                <h3 className="text-lg font-semibold text-gray-700 mb-4">{isEnglishBeta ? "Excluded weekdays" : "送信しない曜日"}</h3>
                                <div className="flex flex-wrap gap-4 mb-6">
                                    {["日", "月", "火", "水", "木", "金", "土"].map(day => (
                                      <label key={day} className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={excludedDays.includes(day)} onChange={() => handleExcludedDayChange(day)} className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"/>{isEnglishBeta ? { "日": "Sun", "月": "Mon", "火": "Tue", "水": "Wed", "木": "Thu", "金": "Fri", "土": "Sat" }[day] : day}</label>
                                    ))}
                                </div>
                                <h3 className="text-lg font-semibold text-gray-700 mb-4">{isEnglishBeta ? "Excluded hours" : "送信しない時間帯"}</h3>
                                <div className="flex items-center gap-4 mb-6">
                                    <input type="time" name="from" value={excludedHours.from} onChange={handleExcludedHourChange} className="p-2 border rounded-md"/>
                                    <span>{isEnglishBeta ? "to" : "〜"}</span>
                                    <input type="time" name="to" value={excludedHours.to} onChange={handleExcludedHourChange} className="p-2 border rounded-md"/>
                                </div>
                                 <button type="submit" className="flex items-center gap-2 bg-blue-600 text-white font-semibold py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"><SaveIcon/> {isEnglishBeta ? "Save exclusion settings" : "除外設定を保存"}</button>
                            </form>
                         </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default SettingsPage;
